import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import {
  OuterContainer,
  ContainerHeading,
  TeamMembersContainer,
  TeamMemberContainer,
  AvatarContainer,
  TextInput,
  EmailAndDepartmentLabel,
  FlexContainer,
  FAIcon,
  CrossButton,
  FieldsContainer,
  PersonalDetailsContainer,
  ErrorMessage,
} from './styles';
import YesNoModal from 'components/Modals/yesNoModal';
import Dropdown from '../../components/Dropdown';
import { Images } from 'util/images';
import { Button } from '../../components';
import { userRoles } from 'util/appConstants';
import { isValidEmail } from 'functions/stringValidation';

export default ({ isDepartmentSettingsPage }) => {
  const {
    settings: { teamMembers, departments },
    invitedUserId,
    updatedUserId,
    deletedUserId,
  } = useSelector((state) => state.settings);
  const { userRole, userId, company } = useSelector((state) => state.auth);

  const { inviteUser, deleteAccount, updateName, updateUserRole, updateUserDepartment, resetUpdatedIds } =
    useActions(actions);

  useEffect(() => {
    if (invitedUserId) {
      toast.success('Member invited successfully');
      resetUpdatedIds();
    }
  }, [invitedUserId]);

  useEffect(() => {
    if (updatedUserId) {
      toast.success('Team member updated successfully');
      resetUpdatedIds();
    }
  }, [updatedUserId]);

  useEffect(() => {
    if (deletedUserId) {
      toast.success('Member removed successfully');
      resetUpdatedIds();
    }
  }, [deletedUserId]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(userRoles.EMPLOYEE);
  const [department, setDepartment] = useState('');
  const [members, setMembers] = useState(teamMembers || []);
  const [departmentsList, setDepartmentsList] = useState(departments || []);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [formError, setFormError] = useState('');

  let inviteUserRoleList = [
    { label: 'Team Member', value: userRoles.EMPLOYEE },
    { label: 'Department Admin', value: userRoles.DEPARTMENT_ADMIN },
  ];
  let teamMembersRole = [
    { label: 'Team Member', value: userRoles.EMPLOYEE },
    { label: 'Department Admin', value: userRoles.DEPARTMENT_ADMIN },
  ];
  if ([userRoles.SUPER_ADMIN, userRoles.RESELLER, userRoles.COMPANY_ADMIN].includes(userRole)) {
    inviteUserRoleList.push({ label: 'Company Admin', value: userRoles.COMPANY_ADMIN });
    teamMembersRole.push({ label: 'Company Admin', value: userRoles.COMPANY_ADMIN });
  }
  if (userRole === userRoles.SUPER_ADMIN && company.reseller === true)
    inviteUserRoleList.push({ label: 'Reseller', value: userRoles.RESELLER });

  useEffect(() => {
    if (!departments?.length) return;
    if (departments.length === 1) setDepartment(departments[0]._id);
    let d = departments.map((department) => {
      if (department.default) setDepartment(department._id);
      return {
        label: department.name ? department.name : department.default ? 'Default Department' : company.name,
        value: department._id,
      };
    });
    setDepartmentsList(d);
  }, [departments]);

  useEffect(() => {
    setMembers(teamMembers);
  }, [teamMembers]);

  const confirmModal = (isConfirmed) => {
    setShowConfirmationModal(false);
    if (!isConfirmed) return;
    if (modalInfo.operation === 'removeUser') {
      deleteAccount(modalInfo.userId);
    } else if (modalInfo.operation === 'updateRole') {
      updateUserRole(modalInfo.userId, { role: modalInfo.role });
    } else if (modalInfo.operation === 'updateDepartment') {
      updateUserDepartment(modalInfo.userId, modalInfo.department);
    }
    setModalInfo({});
  };

  const validateAndInviteUser = () => {
    if (!name) {
      return setFormError('Name is required');
    } else if (!email) {
      return setFormError('Email is required');
    } else if (!isValidEmail(email)) {
      return setFormError('Email is not valid');
    }
    setFormError('');
    let params = {
      name,
      email,
      role,
      department,
      company: company?._id,
    };
    inviteUser(params);
  };

  const setTeamMemberInformation = (index, value, field) => {
    let teamMembers = JSON.parse(JSON.stringify(members));
    teamMembers[index][field] = value;
    setMembers(teamMembers);
  };

  const showDeleteButton = (member) => {
    if (userId === member._id) return false;
    if (member.role === userRoles.SUPER_ADMIN) return false;
    if (
      member.role === userRoles.COMPANY_ADMIN &&
      ![userRoles.SUPER_ADMIN, userRoles.COMPANY_ADMIN, userRoles.RESELLER].includes(userRole)
    )
      return false;
    if (member.role === userRoles.RESELLER && userRole !== userRoles.SUPER_ADMIN) return false;
    return true;
  };

  const renderTeamMembers = () => {
    return members.map((member, index) => (
      <TeamMemberContainer key={member._id} isDepartmentSettingsPage={isDepartmentSettingsPage}>
        <AvatarContainer
          image={member.avatar === '/images/placeholder-avatar.png' ? Images.defaultAvatar : member.avatar}
        ></AvatarContainer>
        {showDeleteButton(member) && (
          <CrossButton>
            <i
              className="fa fa-times"
              onClick={() => {
                setShowConfirmationModal(true);
                setModalInfo({
                  title: 'Confirmation',
                  body: `Do you want to remove the <b>${member.name}</b>?`,
                  operation: 'removeUser',
                  userId: member._id,
                });
              }}
            />
          </CrossButton>
        )}
        <Dropdown
          disabled={
            userId === member._id ||
            member.role === userRoles.SUPER_ADMIN ||
            (member.role === userRoles.COMPANY_ADMIN && userRole === userRoles.DEPARTMENT_ADMIN) ||
            (member.role === userRoles.RESELLER && userRole !== userRoles.SUPER_ADMIN)
          }
          options={
            member.role === userRoles.SUPER_ADMIN
              ? teamMembersRole.concat([{ label: 'Super Admin', value: userRoles.SUPER_ADMIN }])
              : member.role === userRoles.RESELLER || userRole === userRoles.SUPER_ADMIN
              ? teamMembersRole.concat([{ label: 'Reseller', value: userRoles.RESELLER }])
              : teamMembersRole
          }
          value={member.role}
          onChange={(returnedValue) => {
            setShowConfirmationModal(true);
            setModalInfo({
              title: 'Confirmation',
              body: `Do you want to change the user role to <b>${returnedValue.label}</b>?`,
              operation: 'updateRole',
              userId: member._id,
              role: returnedValue.value,
            });
          }}
          fillAndCenter
        />
        {!isDepartmentSettingsPage &&
          [userRoles.SUPER_ADMIN, userRoles.COMPANY_ADMIN, userRoles.RESELLER].includes(userRole) &&
          [userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(member.role) && (
            <Dropdown
              disabled={userId === member._id}
              options={departmentsList}
              value={member.department}
              onChange={(returnedValue) => {
                setShowConfirmationModal(true);
                setModalInfo({
                  title: 'Confirmation',
                  body: `Do you want to change the user department to <b>${returnedValue.label}</b>?`,
                  operation: 'updateDepartment',
                  userId: member._id,
                  department: returnedValue.value,
                });
              }}
              fillAndCenter
            />
          )}
        <PersonalDetailsContainer
          margin={
            [userRoles.SUPER_ADMIN, userRoles.COMPANY_ADMIN, userRoles.RESELLER].includes(member.role) &&
            !isDepartmentSettingsPage
          }
        >
          <FlexContainer>
            <TextInput
              isTeamMemberInput
              isEditing={member.isEditing}
              value={member.name}
              onChange={(e) => setTeamMemberInformation(index, e.target.value, 'name')}
            />
            {!member.isEditing && (
              <FAIcon icon="fa fa-pencil" onClick={() => setTeamMemberInformation(index, true, 'isEditing')}></FAIcon>
            )}
            {member.isEditing && (
              <FAIcon
                icon="fa fa-check"
                onClick={() => {
                  setTeamMemberInformation(index, false, 'isEditing');
                  updateName(member._id, { name: member.name });
                }}
              ></FAIcon>
            )}
          </FlexContainer>

          <EmailAndDepartmentLabel>{member.email}</EmailAndDepartmentLabel>
          {!isDepartmentSettingsPage && <EmailAndDepartmentLabel>{member.departmentName}</EmailAndDepartmentLabel>}
        </PersonalDetailsContainer>
      </TeamMemberContainer>
    ));
  };

  return (
    <OuterContainer>
      {showConfirmationModal && (
        <YesNoModal modalInfo={modalInfo} confirm={(isConfirmed) => confirmModal(isConfirmed)} />
      )}
      <ContainerHeading>Team Settings</ContainerHeading>
      <FieldsContainer>
        <TextInput isEditing={true} placeholder="Name*" onChange={(e) => setName(e.target.value)} value={name} />
        <TextInput
          isEditing={true}
          placeholder="Email Address*"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
        />
        <Dropdown
          width="100%"
          options={inviteUserRoleList}
          onChange={(returnedValue) => setRole(returnedValue.value)}
        />
        {(role === userRoles.DEPARTMENT_ADMIN || role === userRoles.EMPLOYEE) && !isDepartmentSettingsPage && (
          <Dropdown
            width="100%"
            options={departmentsList}
            onChange={(returnedValue) => setDepartment(returnedValue.value)}
          />
        )}
      </FieldsContainer>
      {formError && <ErrorMessage>{formError}</ErrorMessage>}
      <Button
        small
        primaryBlue
        primaryGreenBorder
        removeMargin
        margin="10px 0 0 10px"
        onClick={() => validateAndInviteUser()}
      >
        Invite
      </Button>
      <TeamMembersContainer>{renderTeamMembers()}</TeamMembersContainer>
    </OuterContainer>
  );
};
